/** @jsxImportSource @emotion/react */
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import { css, useTheme } from '@emotion/react';
import DropDownSelect from '@DropDownSelect';
import { useEffect, useReducer, memo } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { getErrorMessage } from '../../Helpers/validation';
import {
  setComponentValidationStatus,
  setSelectedOptions,
  updateValue,
} from '../../Lib/redux/slices/components';
import QuestionText from '../QuestionText/QuestionText';
import { SELECT_COUNTRY_MAPPED_QUESTION_ID } from '../../Data/Country';
import {
  getSelectedOptionId,
  removeDefaultOption,
  updatePhoneNumberCountryCodeFollowingCountrySelection,
} from './Select.helpers';
import { handleAnswerQuestionSideEffectsSaga } from '../../Hooks/actionCreators';
import { setShouldNavigateForward } from '../../Lib/redux/slices/navigationControls';
import { renderMustache } from '../../Helpers/mustache';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';
import mappedQuestionIdIcons from '../../Data/Maps/mappedQuestionIdIcons';

const SelectComponent = ({
  autoAdvance,
  componentId,
  iFrameId,
  subheading,
  formId,
}) => {
  const dispatch = useDispatch();
  const [selectionCount, triggerSelection] = useReducer((x) => x + 1, 0);
  const theme = useTheme();
  const countryCode = useSelector((store) => store.formConfig.countryCode);
  const components = useSelector((store) => store.components);
  const supplierMatches = useSelector((store) => store.supplierMatches);
  const showBrandMatchCount = useSelector(
    (store) => store.features.showBrandMatchCount
  );
  const component = components[`component_${componentId}`];
  const { validation, input, component: settings } = component;
  const { value, options } = input;
  const { text, displayQuestionLabelAsPlaceholder, mappedQuestionId } =
    settings;

  const { displayQuestionTextInsideTextfield } = component.component;
  const errorMessage = getErrorMessage(validation.constraints);
  const [defaultOptionValue, optionsWithoutDefault] =
    removeDefaultOption(options);

  const dropDownSelectExperimentName = useGetExperimentName({
    componentId,
    componentName: 'DropDownSelect',
  });

  const handleSelect = (params) => {
    const { value: newValue } = params;
    const selectedOptionId = getSelectedOptionId(newValue, options);
    batch(() => {
      dispatch(
        updateValue({
          componentId,
          value: newValue,
        })
      );
      dispatch(
        setSelectedOptions({
          componentId,
          selectedOptions: [String(selectedOptionId)],
        })
      );
      dispatch(
        setComponentValidationStatus({
          componentId,
          iFrameId,
          formId,
        })
      );
      dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
      triggerSelection();
    });
    if (mappedQuestionId === SELECT_COUNTRY_MAPPED_QUESTION_ID) {
      updatePhoneNumberCountryCodeFollowingCountrySelection({
        optionValue: newValue,
        options,
        dispatch,
        components,
        countryCode,
      });
    }
  };

  useEffect(() => {
    if (selectionCount > 0 && autoAdvance) {
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }
  }, [selectionCount]);

  const handleBlur = () => {
    batch(() => {
      dispatch(
        setComponentValidationStatus({
          componentId,
          countryCode,
          iFrameId,
          formId,
        })
      );
      dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
    });
  };

  return (
    <>
      {!displayQuestionLabelAsPlaceholder &&
        !displayQuestionTextInsideTextfield && (
          <QuestionText
            heading={renderMustache({
              template: text,
              components,
              currentComponent: component,
              iFrameId,
              formId,
              supplierMatches,
              showBrandMatchCount,
            })}
            subheading={renderMustache({
              template: subheading,
              components,
              currentComponent: component,
              iFrameId,
              formId,
              supplierMatches,
              showBrandMatchCount,
            })}
          />
        )}
      <Box
        css={css({
          marginTop: theme.mvf.spacing.medium,
          marginBottom: theme.mvf.spacing.small,
        })}
        data-testid="selectContainer"
      >
        <DropDownSelect
          errorMessage={errorMessage}
          id={`${componentId}`}
          iconType={mappedQuestionIdIcons[mappedQuestionId]}
          onChange={(params) => handleSelect({ ...params })}
          label={
            displayQuestionLabelAsPlaceholder ||
            displayQuestionTextInsideTextfield
              ? text
              : defaultOptionValue
          }
          fullWidth
          onBlur={() => handleBlur()}
          value={value || ''}
          experimentName={dropDownSelectExperimentName}
          shrinkLabel={displayQuestionTextInsideTextfield}
        >
          {optionsWithoutDefault.map((option) => {
            return (
              <MenuItem
                key={`${option.id}`}
                value={option.value}
                css={css({
                  fontSize: theme.mvf.fontSize.medium,
                })}
              >
                {option.value}
              </MenuItem>
            );
          })}
        </DropDownSelect>
      </Box>
    </>
  );
};

const Select = memo(SelectComponent);

export default Select;
